import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { LinkedOaStore } from '@app/pages/chat/linked-oa.store';
import { LinkedOAInfo } from '@app/pages/chat/types/chat';
import { BroadcastService } from '@app/services/broadcast.service';

import { StrangeNumberWarningComponent } from '../../../../pages/chat/components/strange-number-warning/strange-number-warning.component';
import { getColorById, isDevice, isUrl } from '../../../../utils/utils';
import { AvatarComponent } from '../../../avatar/avatar.component';
import { AppIconComponent } from '../../../icon/icon.component';
import { TammiDeviceBadgeComponent } from '../../../tammi-device-badge/tammi-device-badge.component';
import { CALL_TYPE } from '../../constants';
import { CallUserResolverService } from '../../services/call-user-resolver.service';
import { CallInvitation, CallParticipant } from '../../types/call';

@Component({
  selector: 'app-call-invitation',
  standalone: true,
  imports: [
    AvatarComponent,
    AppIconComponent,
    TammiDeviceBadgeComponent,
    StrangeNumberWarningComponent,
  ],
  templateUrl: './call-invitation.component.html',
  styleUrl: './call-invitation.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CallInvitationComponent implements OnInit, OnDestroy {
  invitation = input.required<CallInvitation>();
  participants = input.required<CallParticipant[]>();
  isWaiting = input(false);
  isInMeeting = input(false);

  CALL_TYPE = CALL_TYPE;

  private callUserInfoResolver = inject(CallUserResolverService);
  private linkedOaStore = inject(LinkedOaStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly broadcastService = inject(BroadcastService);

  readonly linkedOaInfo = computed<LinkedOAInfo | null>(() => {
    const oaUserId = this.invitation()?.extendedData?.oaUserId;
    if (!oaUserId) return null;
    return this.linkedOaStore.getLinkedOa(oaUserId) ?? null;
  });

  readonly internetSource = computed(
    () => this.invitation()?.extendedData?.source?.toLocaleLowerCase() === 'internet',
  );
  readonly address = computed(() => this.invitation()?.extendedData?.address);

  acceptEvt = output<string>();
  rejectEvt = output<string>();
  rejectTimeoutEvt = output<void>();

  private pendingTimeoutId: ReturnType<typeof setTimeout> | null = null;

  isInContact = this.callUserInfoResolver.isInContact;
  callerName = this.callUserInfoResolver.callerName;
  callerAvatar = this.callUserInfoResolver.callerAvatar;
  callerId = computed(() => this.participants()[0]?.userId ?? '');

  readonly hasValidAvatar = computed(() => {
    const avatar = this.participants()[0]?.avatar;
    if (!avatar || typeof avatar !== 'string') return false;
    const trimmed = avatar.trim();
    if (trimmed.length === 0) return false;
    return isUrl(trimmed) || trimmed.startsWith('/');
  });

  readonly avatarBgError = signal(false);

  private readonly hasLoadedAvatar = computed(() => this.hasValidAvatar() && !this.avatarBgError());

  readonly isDevice = computed(() => {
    return this.callerId() ? isDevice(this.callerId()) : false;
  });

  readonly backgroundImage = computed(() => {
    if (this.hasLoadedAvatar()) return `url(${this.participants()[0]!.avatar})`;
    if (this.isDevice()) return 'url(assets/images/default_device.png)';
    return null;
  });

  readonly backgroundColor = computed(() => {
    if (this.hasLoadedAvatar()) return null;
    if (this.isDevice()) return 'var(--color-bg-hover)';
    return getColorById(this.callerId())[2];
  });

  readonly shapesColor = computed(() => {
    if (this.hasLoadedAvatar()) return null;
    return getColorById(this.callerId())[3];
  });

  handleAccept() {
    const callId = this.invitation()?.callId;
    if (callId) {
      this.acceptEvt.emit(callId);
    }
  }

  handleReject() {
    const callId = this.invitation()?.callId;
    if (callId) {
      this.rejectEvt.emit(callId);
    }
  }

  ngOnInit(): void {
    const pending = this.invitation();

    const expiresAt = pending.receivedAt.getTime() + pending.timeoutSeconds * 1000;
    let remaining = expiresAt - Date.now();
    remaining = remaining > 0 ? remaining : 0;

    console.log('[DEBUG-CALL]', 'Call invitation manual time out set timeout with', remaining);
    this.pendingTimeoutId = setTimeout(() => {
      this.rejectTimeoutEvt.emit();
    }, remaining);

    this.broadcastService.meeting$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res) => {
      if (res.action === 'start-meeting') {
        this.handleReject();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.pendingTimeoutId) {
      clearTimeout(this.pendingTimeoutId);
    }
  }
}
